import { useEffect, useRef, useState, type ReactNode } from 'react'

/*
 * Pull down to insist.
 *
 * The app refetches on its own - coming back to it, and on a timer while a
 * game runs - so this is not how the screen stays current. It is how you ask
 * when you do not believe it, which on a Saturday afternoon is a real feeling
 * and worth answering.
 *
 * It reloads the page when a newer build is being served, because "give me the
 * latest" means the latest code as much as the latest score, and sw.js is
 * deliberately not a cache so a reload is the whole of it.
 */

/** How far to pull before it counts. */
const THRESHOLD = 64
/** How far the content will move, however hard you pull. */
const MAX = 92

export function PullToRefresh({
  onRefresh,
  children,
}: {
  onRefresh: () => Promise<void> | void
  children: ReactNode
}) {
  const [pull, setPull] = useState(0)
  const [busy, setBusy] = useState(false)
  const start = useRef<{ x: number; y: number } | null>(null)
  const armed = useRef(false)

  useEffect(() => {
    const down = (e: TouchEvent) => {
      const t = e.touches[0]
      if (!t) return
      // Only from a page already at the top; anywhere else this is a scroll.
      armed.current = window.scrollY <= 0 && !busy
      start.current = { x: t.clientX, y: t.clientY }
    }

    const move = (e: TouchEvent) => {
      const t = e.touches[0]
      const s = start.current
      if (!armed.current || !s || !t) return

      const dy = t.clientY - s.y
      const dx = t.clientX - s.x

      /*
       * THE GRID SCROLLS SIDEWAYS. A swipe across its columns carries a few
       * pixels of vertical drift, and without this the week would jump under
       * the thumb every time somebody looked at Sunday's games. Anything more
       * horizontal than vertical is not a pull, and it disarms rather than
       * waiting - a gesture that begins sideways stays sideways.
       */
      if (Math.abs(dx) > Math.abs(dy)) {
        armed.current = false
        setPull(0)
        return
      }

      // An upward drag is an ordinary scroll; let go of it entirely.
      if (dy <= 0 || window.scrollY > 0) {
        armed.current = false
        setPull(0)
        return
      }

      // Resisted, so it feels attached to something rather than free.
      e.preventDefault()
      setPull(Math.min(dy * 0.5, MAX))
    }

    const up = async () => {
      const reached = pull >= THRESHOLD
      armed.current = false
      start.current = null
      if (!reached) {
        setPull(0)
        return
      }
      // Held at the threshold while it works, so the spinner has somewhere to
      // be. Snapping back first would make a fast refresh look like nothing.
      setBusy(true)
      setPull(THRESHOLD)
      try {
        await onRefresh()
      } finally {
        setBusy(false)
        setPull(0)
      }
    }

    // passive: false on move, because it is the one that preventDefaults.
    document.addEventListener('touchstart', down, { passive: true })
    document.addEventListener('touchmove', move, { passive: false })
    document.addEventListener('touchend', up)
    document.addEventListener('touchcancel', up)
    return () => {
      document.removeEventListener('touchstart', down)
      document.removeEventListener('touchmove', move)
      document.removeEventListener('touchend', up)
      document.removeEventListener('touchcancel', up)
    }
  }, [pull, busy, onRefresh])

  const ready = pull >= THRESHOLD

  return (
    <div className="relative flex flex-1 flex-col">
      {/*
       * Mint, because this is about time - how current the screen is - and
       * that is the one thing mint is for.
       */}
      <div
        aria-hidden={pull === 0}
        className="pointer-events-none absolute inset-x-0 top-0 flex items-end justify-center overflow-hidden"
        style={{ height: pull, opacity: pull === 0 ? 0 : 1 }}
      >
        <span className="slate-num pb-2 text-[9px] font-bold tracking-[0.14em] text-live-deep uppercase">
          {busy ? 'Refreshing' : ready ? 'Release' : 'Pull to refresh'}
        </span>
      </div>

      <div
        className="flex flex-1 flex-col"
        style={{
          transform: `translateY(${pull}px)`,
          // No transition while the finger is down, or the content lags behind
          // the thumb; one on the way back, so it settles instead of snapping.
          transition: pull === 0 || busy ? 'transform 180ms ease-out' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  )
}
